import { Avatar, Button } from "antd";
import { UserPublishResultResponseStatus } from "@apis/generated/openapi";
import { Trans } from "react-i18next";
import { normalizeNameWithAt } from "@utils/util";
import { useFormatDateTime } from "@hooks/useFormatDateTime";
import { IconLineClock } from "@kalodata/kds-design-icons";
import { DarkTooltip } from "@components/DarkTooltip";
import { type LinkedAccount } from "@store/linkedAccountSlice";
import { REGION_TIMEZONE_MAP, DEFAULT_TIMEZONE } from "@src/config/timezone";
import { SalesMetricsRow } from "./SalesMetricsRow";
import { StatusBadge } from "./StatusBadge";
import { type PublisherTab, type PublishRecord } from "./types";

export interface PublishRecordCardProps {
  tab: PublisherTab;
  item: PublishRecord;
  accounts: LinkedAccount[];
  onUnschedule: (item: PublishRecord) => void;
  onRepublish: (item: PublishRecord) => void;
}

export const PublishRecordCard = (props: PublishRecordCardProps) => {
  const { tab, item, accounts, onRepublish, onUnschedule } = props;
  const { status, accountName: accountNameValue } = item;
  const formatDateTime = useFormatDateTime();
  const isFailed = status === UserPublishResultResponseStatus.FAIL;
  const showScheduledAction = tab === "scheduled" && status === UserPublishResultResponseStatus.PENDING_PUBLISH;
  const showRepublishAction = tab === "scheduled" && isFailed;
  const accountName = normalizeNameWithAt(accountNameValue);
  const accountFallback = (item.accountName || "U").slice(0, 1).toUpperCase();

  const targetAccount = accounts.find((acc) => acc.id === item.linkId);
  const targetTimezone = targetAccount?.region
    ? REGION_TIMEZONE_MAP[targetAccount.region] || DEFAULT_TIMEZONE
    : DEFAULT_TIMEZONE;

  return (
    <div className="group flex flex-col gap-[12px] rounded-[8px] border border-kds-border bg-kds-bg-card p-[16px]">
      <div className="flex w-full items-center gap-[16px]">
        <div className="relative h-[160px] w-[90px] shrink-0 overflow-hidden rounded-[4px] bg-kds-bg-element">
          {item.assetCoverImageUrl ? (
            <img className="size-full object-cover" src={item.assetCoverImageUrl} alt="" />
          ) : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-between self-stretch">
          <div className="flex w-full flex-col gap-[8px]">
            <DarkTooltip title={item.productTitle || "--"}>
              <p className="truncate text-[20px] font-semibold leading-[24px] text-kds-fg">
                {item.productTitle || "--"}
              </p>
            </DarkTooltip>

            <div className="flex items-center gap-[24px]">
              <div className="flex items-center gap-[4px]">
                <Avatar src={item.accountImage || undefined} size={24}>
                  {accountFallback}
                </Avatar>
                <span className="text-[14px] font-normal leading-[20px] text-kds-fg">{accountName || "--"}</span>
              </div>

              <div className="flex items-center gap-[4px] text-[14px] font-normal leading-[20px]">
                <IconLineClock size={16} className="text-kds-fg-soft" />
                {tab === "published" ? (
                  <>
                    <span className="text-kds-fg-soft">
                      <Trans>Published:</Trans>
                    </span>
                    <span className="text-kds-fg">
                      {formatDateTime(item.postTime, "YYYY-MM-DD HH:mm", "--", targetTimezone)}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-kds-fg-soft">
                      <Trans>Est. Publish:</Trans>
                    </span>
                    <span className="text-kds-fg">
                      {formatDateTime(item.publishTime, "YYYY-MM-DD HH:mm", "--", targetTimezone)}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* 互动指标占位 slot：接口暂未提供 Views/Likes/Shares/Comments/Followers Gained，预留高度保持垂直节奏 */}
            <div aria-hidden className="h-[21px] w-full" />
          </div>

          <SalesMetricsRow item={item} />
        </div>
      </div>

      <div className="h-px w-full bg-kds-border" />

      <div className="flex w-full items-center justify-between">
        <StatusBadge tab={tab} status={status} />

        {showScheduledAction ? (
          <Button
            className="h-[28px]! rounded-[6px] border-kds-border-solid bg-kds-bg-element px-[10px] text-[14px] font-medium leading-[16px] text-kds-fg opacity-0 shadow-[0_1px_3px_0_var(--kds-shadow)] transition-opacity group-hover:opacity-100"
            onClick={() => onUnschedule(item)}
          >
            <Trans>Unschedule</Trans>
          </Button>
        ) : null}

        {showRepublishAction ? (
          <Button
            className="h-[28px] rounded-[6px] border-kds-border-solid bg-kds-bg-element px-[10px] text-[14px] font-medium leading-[16px] text-kds-fg opacity-0 shadow-[0_1px_3px_0_var(--kds-shadow)] transition-opacity group-hover:opacity-100"
            onClick={() => onRepublish(item)}
          >
            <Trans>Republish</Trans>
          </Button>
        ) : null}
      </div>
    </div>
  );
};
